<script lang="ts">
  /* THE LAYER CACHE, IN FULL.
   *
   * The chart's CACHE panel answers "is it working" while you are holding the
   * iPad. This answers "what is it doing" — every layer, its configuration
   * beside its behaviour, what is stored, and what has gone past in the last few
   * minutes.
   *
   * ═══ CONFIG NEXT TO BEHAVIOUR, ALWAYS ═══
   *
   * A hit rate on its own means nothing: a feed with a six-hour TTL and one with
   * a one-minute TTL are SUPPOSED to have very different numbers. So every row
   * carries what it is set to and what it did, and nobody has to hold the
   * settings in their head to read the figures.
   *
   * ═══ THE LIVE VIEW IS A CURSOR, NOT A REFETCH ═══
   *
   * `/admin/events?since=` returns only what is new, so watching traffic costs
   * one small request every couple of seconds rather than the whole ring. The
   * cursor comes back even when nothing happened, so a quiet minute does not
   * make the page re-request what it already has.
   */
  import { sources } from '@gennetay/chart-core/sources';

  interface FeedRow {
    id: string;
    title: string;
    kind: string;
    attribution: string;
    available: boolean;
    reason?: string;
    config: {
      coverage: { west: number; south: number; east: number; north: number } | null;
      ttlSeconds: number;
      staleSeconds: number;
      store: string;
      secrets: string[];
    };
    stats: {
      hit: number;
      miss: number;
      stale: number;
      fail: number;
      orphan: number;
      writefail: number;
      hitRate: number | null;
      entries: number | null;
      newestAt: number | null;
    };
  }
  interface ChartRow {
    id: string;
    name: string;
    bounds: { west: number; south: number; east: number; north: number } | null;
    squares: number;
    charted: number;
    blank: number;
    hits: number;
  }
  interface Ev {
    id: number;
    at: number;
    kind: string;
    target: string;
    outcome: string;
    ms: number;
    bytes: number;
    status: number;
  }
  interface Stats {
    now: number;
    storage: { blobs: { count: number; bytes: number }; squares: number; events: number; kvEntries: number | null };
    feeds: FeedRow[];
    charts: ChartRow[];
    tiles: Record<string, number>;
    counters: Record<string, number>;
  }

  let stats = $state<Stats | null>(null);
  let events = $state<Ev[]>([]);
  let error = $state<string | null>(null);
  let live = $state(true);
  let cursor = 0;
  let open = $state<string | null>(null);

  const has = $derived(sources().has('feeds'));

  async function loadStats() {
    try {
      const r = await sources().fetch('feeds', 'admin/stats', {
        signal: AbortSignal.timeout(15_000)
      });
      if (!r.ok) throw new Error(`the backend answered ${r.status}`);
      stats = (await r.json()) as Stats;
      error = null;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  async function loadEvents() {
    try {
      const r = await sources().fetch('feeds', `admin/events?since=${cursor}&limit=60`, {
        signal: AbortSignal.timeout(15_000)
      });
      if (!r.ok) return;
      const j = (await r.json()) as { cursor: number; events: Ev[] };
      if (j.events.length) {
        /* Newest first, and CAPPED. An unbounded list is a memory leak on a
         * page somebody leaves open on a chart table for a week. */
        events = [...j.events, ...events].slice(0, 300);
      }
      cursor = Math.max(cursor, j.cursor ?? 0);
    } catch {
      /* A dropped poll is not worth a banner — the next one will catch up. */
    }
  }

  $effect(() => {
    if (!has) return;
    void loadStats();
    void loadEvents();
    const a = setInterval(() => void loadStats(), 10_000);
    const b = setInterval(() => {
      if (live) void loadEvents();
    }, 2000);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  });

  const bytes = (n: number | null | undefined): string => {
    if (n === null || n === undefined || !Number.isFinite(n)) return '—';
    const u = ['B', 'kB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < u.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
  };
  const num = (n: number | null | undefined): string =>
    n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en');
  const pct = (n: number | null | undefined): string =>
    n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`;
  const dur = (s: number): string =>
    s >= 86400 ? `${Math.round(s / 86400)} d` : s >= 3600 ? `${Math.round(s / 3600)} h` : `${Math.round(s / 60)} min`;
  const clock = (t: number) => new Date(t).toISOString().slice(11, 19);
  const box = (b: FeedRow['config']['coverage']) =>
    b ? `${b.south.toFixed(1)}…${b.north.toFixed(1)}°N  ${b.west.toFixed(1)}…${b.east.toFixed(1)}°E` : 'global';
</script>

<main>
  <header>
    <h1>LAYER CACHE</h1>
    <span class="host">{has ? sources().base('feeds') : 'no backend configured'}</span>
  </header>

  {#if !has}
    <p class="empty">
      This build has no <code>feeds</code> source, so there is no backend to
      report on. Set <code>VITE_LAYER_CACHE</code> and rebuild.
    </p>
  {:else if error}
    <p class="empty bad">{error}</p>
  {:else if !stats}
    <p class="empty">reading…</p>
  {:else}
    <section class="tiles">
      <div class="stat"><b>{num(stats.storage.squares)}</b><span>chart squares</span></div>
      <div class="stat"><b>{num(stats.storage.blobs.count)}</b><span>stored bodies</span></div>
      <div class="stat"><b>{bytes(stats.storage.blobs.bytes)}</b><span>on disk</span></div>
      <div class="stat">
        <b>{pct(
          (stats.tiles.hits ?? 0) + (stats.tiles.misses ?? 0) > 0
            ? (stats.tiles.hits ?? 0) / ((stats.tiles.hits ?? 0) + (stats.tiles.misses ?? 0))
            : null
        )}</b><span>tile hit rate</span>
      </div>
      <div class="stat"><b>{num(stats.tiles.blankLearned)}</b><span>blanks learned</span></div>
      <div class="stat"><b>{num(stats.storage.events)}</b><span>logged events</span></div>
    </section>

    <h2>FEEDS</h2>
    <!-- Config and behaviour in one row, deliberately: a hit rate without the
         TTL beside it is not a number anyone can judge. -->
    <table>
      <thead>
        <tr>
          <th>feed</th><th>kind</th><th>coverage</th><th class="n">ttl</th>
          <th class="n">hit</th><th class="n">miss</th><th class="n">stale</th>
          <th class="n">fail</th><th class="n">rate</th><th></th>
        </tr>
      </thead>
      <tbody>
        {#each stats.feeds as f (f.id)}
          <tr class:off={!f.available} onclick={() => (open = open === f.id ? null : f.id)}>
            <td><b>{f.id}</b><span class="sub">{f.title}</span></td>
            <td>{f.kind}</td>
            <td class="mono sm">{box(f.config.coverage)}</td>
            <td class="n">{dur(f.config.ttlSeconds)}</td>
            <td class="n">{num(f.stats.hit)}</td>
            <td class="n">{num(f.stats.miss)}</td>
            <td class="n" class:warn={f.stats.stale > 0}>{num(f.stats.stale)}</td>
            <td class="n" class:bad={f.stats.fail > 0}>{num(f.stats.fail)}</td>
            <td class="n">{pct(f.stats.hitRate)}</td>
            <td class="n">{open === f.id ? '▾' : '▸'}</td>
          </tr>
          {#if open === f.id}
            <tr class="detail">
              <td colspan="10">
                <dl>
                  <div><dt>attribution</dt><dd>{f.attribution}</dd></div>
                  <div><dt>store</dt><dd>{f.config.store}</dd></div>
                  <div><dt>stale window</dt><dd>{dur(f.config.staleSeconds)}</dd></div>
                  <div>
                    <dt>secrets</dt>
                    <dd>{f.config.secrets.length ? f.config.secrets.join(', ') : 'none'}</dd>
                  </div>
                  <div>
                    <dt>cached entries</dt>
                    <!-- Genuinely unknown for KV-backed feeds: KV has no cheap
                         count. Null, not 0 — those are different facts. -->
                    <dd>{f.stats.entries === null ? 'unknown (KV has no count)' : num(f.stats.entries)}</dd>
                  </div>
                  <div><dt>newest entry</dt><dd>{f.stats.newestAt ? clock(f.stats.newestAt) : '—'}</dd></div>
                  <div>
                    <dt>orphans</dt>
                    <dd class:warn={f.stats.orphan > 0}>{num(f.stats.orphan)} <span class="sub">index knew of a body the store did not</span></dd>
                  </div>
                  <div>
                    <dt>write failures</dt>
                    <dd class:warn={f.stats.writefail > 0}>{num(f.stats.writefail)} <span class="sub">answered anyway, but not cached</span></dd>
                  </div>
                  {#if !f.available}<div><dt>unavailable</dt><dd class="bad">{f.reason}</dd></div>{/if}
                </dl>
              </td>
            </tr>
          {/if}
        {/each}
      </tbody>
    </table>

    <h2>CHARTS</h2>
    <table>
      <thead>
        <tr>
          <th>chart</th><th>bounds</th><th class="n">squares</th><th class="n">with chart</th>
          <th class="n">empty</th><th class="n">served</th>
        </tr>
      </thead>
      <tbody>
        {#each stats.charts as c (c.id)}
          <tr>
            <td><b>{c.id}</b><span class="sub">{c.name}</span></td>
            <td class="mono sm">{box(c.bounds)}</td>
            <td class="n">{num(c.squares)}</td>
            <td class="n">{num(c.charted)}</td>
            <!-- Squares the service says have no chart. Never refetched, always
                 404 — a cache that looks large can be mostly this. -->
            <td class="n">{num(c.blank)}</td>
            <td class="n">{num(c.hits)}</td>
          </tr>
        {/each}
        {#if stats.charts.length === 0}
          <tr><td colspan="6" class="sub">nothing cached yet — open the chart and pan</td></tr>
        {/if}
      </tbody>
    </table>

    <h2>PASS-THROUGH &amp; UPSTREAM</h2>
    <section class="tiles">
      <div class="stat"><b>{num(stats.tiles.misses)}</b><span>fetched upstream</span></div>
      <div class="stat"><b>{num(stats.tiles.stored)}</b><span>stored</span></div>
      <div class="stat"><b>{num(stats.tiles.blankServed)}</b><span>blank served</span></div>
      <div class="stat"><b>{num(stats.tiles.outOfBounds)}</b><span>outside a chart</span></div>
      <div class="stat" class:bad={(stats.tiles.upstreamError ?? 0) > 0}>
        <b>{num(stats.tiles.upstreamError)}</b><span>upstream sent junk</span>
      </div>
      <div class="stat" class:bad={(stats.tiles.upstreamUnreachable ?? 0) > 0}>
        <b>{num(stats.tiles.upstreamUnreachable)}</b><span>upstream unreachable</span>
      </div>
    </section>

    <h2>
      LIVE
      <button class="toggle" class:on={live} onclick={() => (live = !live)}>
        {live ? '● streaming' : '○ paused'}
      </button>
    </h2>
    <table class="log">
      <tbody>
        {#each events as e (e.id)}
          <tr>
            <td class="mono sm">{clock(e.at)}</td>
            <td>{e.kind}</td>
            <td><span class="pill {e.outcome}">{e.outcome}</span></td>
            <td class="mono sm grow">{e.target}</td>
            <td class="n sm">{e.status}</td>
            <td class="n sm">{e.ms} ms</td>
            <td class="n sm">{e.bytes ? bytes(e.bytes) : ''}</td>
          </tr>
        {/each}
        {#if events.length === 0}
          <tr><td colspan="7" class="sub">nothing yet — traffic appears here as it happens</td></tr>
        {/if}
      </tbody>
    </table>
  {/if}
</main>

<style>
  main {
    max-width: 1100px;
    margin: 0 auto;
    padding: 20px 16px 60px;
    color: var(--ink);
    font: 13px/1.5 var(--sans, system-ui, sans-serif);
  }
  header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    border-bottom: 1px solid var(--line);
    padding-bottom: 10px;
  }
  h1 {
    font: 14px/1 var(--mono);
    letter-spacing: 0.12em;
    margin: 0;
  }
  .host {
    font: 11px/1 var(--mono);
    color: var(--sub);
  }
  h2 {
    font: 11px/1 var(--mono);
    letter-spacing: 0.12em;
    color: var(--sub);
    margin: 26px 0 8px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 8px;
    margin-top: 14px;
  }
  .stat {
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 10px 12px;
  }
  .stat b {
    display: block;
    font: 18px/1.2 var(--mono);
    font-weight: 500;
  }
  .stat span {
    font-size: 10px;
    color: var(--sub);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    /* Wide tables scroll inside their own box rather than the page. */
    display: block;
    overflow-x: auto;
  }
  th {
    text-align: left;
    font: 10px/1 var(--mono);
    letter-spacing: 0.08em;
    color: var(--sub);
    padding: 6px 8px;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
  td {
    padding: 7px 8px;
    border-bottom: 1px solid color-mix(in srgb, var(--line) 55%, transparent);
    vertical-align: top;
    white-space: nowrap;
  }
  tbody tr:not(.detail):hover {
    background: color-mix(in srgb, var(--line) 35%, transparent);
    cursor: pointer;
  }
  tr.off {
    opacity: 0.5;
  }
  .n {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: var(--mono);
  }
  .mono {
    font-family: var(--mono);
  }
  .sm {
    font-size: 11px;
  }
  .grow {
    width: 99%;
  }
  .sub {
    display: block;
    font-size: 10px;
    color: var(--sub);
    font-weight: 400;
  }
  .warn {
    color: var(--am);
  }
  .bad {
    color: var(--bad, #ff6a5a);
  }
  .detail td {
    background: color-mix(in srgb, var(--line) 25%, transparent);
  }
  .detail dl {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 8px 20px;
  }
  .detail dt {
    font-size: 10px;
    color: var(--sub);
  }
  .detail dd {
    margin: 0;
    font-family: var(--mono);
    font-size: 12px;
    white-space: normal;
  }
  .pill {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 3px;
    font: 10px/1.6 var(--mono);
    border: 1px solid currentColor;
  }
  .pill.hit { color: #4fd1c5; }
  .pill.miss { color: var(--sub); }
  .pill.stale { color: var(--am); }
  .pill.blank { color: #7f8fa6; }
  .pill.error { color: var(--bad, #ff6a5a); }
  .pill.out-of-bounds { color: #9a86c9; }
  .toggle {
    background: none;
    border: 1px solid var(--line);
    border-radius: 4px;
    color: var(--sub);
    font: 10px/1 var(--mono);
    padding: 4px 8px;
    cursor: pointer;
  }
  .toggle.on {
    color: #4fd1c5;
    border-color: currentColor;
  }
  .log td {
    padding: 4px 8px;
  }
  .empty {
    color: var(--sub);
    margin-top: 30px;
  }
  code {
    font-family: var(--mono);
    font-size: 11px;
  }
</style>
